import { isAiProspect } from "@crm-src/lib/leads-api"
import type { Lead } from "@crm-src/types/lead"

/**
 * The Prospects area is split into TWO sibling CRM pages instead of one page
 * with an All / Regular / AI origin filter (2026-06-05):
 *
 *   • "regular" → /crm/prospects      — human-entered prospects
 *   • "ai"      → /crm/ai-prospects    — prospects the autonomous XOP sales
 *                                        agent created (Sales ▸ Leads →
 *                                        Convert to Prospect)
 *
 * Both routes render the SAME `<ProspectsListPage>` parameterised by this
 * config — there is no duplicated list/table/bulk code. The page pins the
 * crm-api `ai_generated` filter server-side per origin so counts + pagination
 * stay per-page (not just the visible slice). Keep the AI markers in lockstep
 * with `AI_PROSPECT_SOURCES` / `isAiProspect` (`@crm-src/lib/leads-api`),
 * crm-api `Customer::AI_PROSPECT_SOURCES`, and the dashboard server's
 * `AI_PROSPECT_SOURCE`. See docs/crm.md → "Prospects: regular vs AI split".
 */
export type ProspectOrigin = "regular" | "ai"

export interface ProspectOriginConfig {
  origin: ProspectOrigin
  /** crm-api `ai_generated` filter. `false` → regular scope (DELIBERATELY keeps
   * NULL-source rows — a hand-entered prospect with no source is "regular");
   * `true` → AI scope. Mirrors `Customer.not_ai_generated` / `Customer.ai_generated`. */
  aiGenerated: boolean
  /** Route this origin's list lives at — a sibling of (NOT a child of)
   * `/crm/prospects`, so it can never shadow the `/crm/prospects/:id` detail. */
  listPath: string
  /** `<PageHeader>` title. */
  title: string
  /** `<PageHeader>` description. */
  description: string
  /** Empty-state heading (no rows + no active search). */
  emptyTitle: string
  /** Empty-state body (no rows + no active search). */
  emptyBody: string
  /** Search-input placeholder. */
  searchPlaceholder: string
  /** Whether humans can manually add / import here. Only regular prospects are
   * hand-entered; AI prospects are born from the agent's Convert-to-Prospect,
   * so the AI page is manage-only (view / edit / convert / delete / bulk). */
  allowCreate: boolean
  /** When true, deleting a prospect here ALSO releases the originating
   * Sales-agent lead (clears its exported state so it returns to the Sales
   * Leads list) — the reverse of Convert-to-Prospect. AI-only; a regular
   * prospect has no Sales lead behind it. See `lib/ai-prospect-release.ts`. */
  releaseOnDelete: boolean
  /** Row-menu / button label for the delete action. */
  deleteActionLabel: string
  /** Delete confirm-dialog title. */
  deleteTitle: string
  /** Delete confirm-dialog body. */
  deleteDescription: string
  /** Delete confirm-dialog confirm-button label. */
  deleteConfirmLabel: string
  /** Toast shown after a successful single delete. */
  deleteSuccessMessage: string
}

export const PROSPECT_ORIGIN_CONFIG: Record<ProspectOrigin, ProspectOriginConfig> = {
  regular: {
    origin: "regular",
    aiGenerated: false,
    listPath: "/crm/prospects",
    title: "Prospects",
    description: "Manage your leads and potential customers",
    emptyTitle: "No prospects found",
    emptyBody:
      "Get started by adding your first prospect to track and manage your sales pipeline.",
    searchPlaceholder: "Search prospects by name, email, or company...",
    allowCreate: true,
    releaseOnDelete: false,
    deleteActionLabel: "Delete Prospect",
    deleteTitle: "Delete Prospect",
    // crm-api `leads#destroy` calls `soft_delete!`: it stamps `deleted_at`, and
    // `Customer`'s `default_scope` (`not_deleted.reseller_visible`) drops the
    // row from ordinary queries. The record survives and nothing on disk is
    // erased, so this copy describes the list removal, not a destruction.
    deleteDescription:
      "The prospect is removed from the CRM lists. The record itself is kept, so an administrator can restore it.",
    deleteConfirmLabel: "Delete Prospect",
    deleteSuccessMessage: "Prospect deleted successfully",
  },
  ai: {
    origin: "ai",
    aiGenerated: true,
    listPath: "/crm/ai-prospects",
    title: "AI Prospects",
    description: "Prospects generated by the autonomous XOP sales agent",
    emptyTitle: "No AI prospects yet",
    emptyBody:
      "AI prospects appear here when the autonomous sales agent qualifies a lead. Qualify leads from Sales ▸ Leads → Qualify for CRM.",
    searchPlaceholder: "Search AI prospects by name, email, or company...",
    allowCreate: false,
    releaseOnDelete: true,
    deleteActionLabel: "Remove from CRM",
    deleteTitle: "Remove from CRM",
    deleteDescription:
      "This removes the prospect from the CRM. Its lead returns to the Sales AI list as active — so you (or the agent) can keep working it, or re-add it to the CRM later.",
    deleteConfirmLabel: "Remove from CRM",
    deleteSuccessMessage: "Removed from CRM — the lead is back on the Sales list",
  },
}

export function prospectOriginConfig(origin: ProspectOrigin): ProspectOriginConfig {
  return PROSPECT_ORIGIN_CONFIG[origin]
}

/**
 * Which list a prospect belongs to. Used so a prospect detail page's "Back"
 * (and post-convert / post-delete navigation) returns to the correct page —
 * an AI-generated prospect lands back on `/crm/ai-prospects`.
 */
export function prospectOriginForLead(
  lead: Pick<Lead, "source"> | null | undefined,
): ProspectOrigin {
  return isAiProspect(lead) ? "ai" : "regular"
}

/** The list path a prospect's detail page should return to. */
export function prospectListPathForLead(
  lead: Pick<Lead, "source"> | null | undefined,
): string {
  return PROSPECT_ORIGIN_CONFIG[prospectOriginForLead(lead)].listPath
}
